import { uniq } from "lodash-es";

import { isNotEmptyString } from "@mapmycustomers/shared/util/assert";

// Terms pre-filtering the drill-down query. A form cannot be used verbatim: "patient outcome" was
// also counted in "patient-outcome", which the query would have to find without that exact run of
// characters. So the query narrows on a single word and `matchedFormsPattern` narrows the returned
// page down to the actual matches.
//
// The word picked is the form's longest. That is a heuristic, not a guarantee: `$contains` is
// `$in`, which for a note resolves to an Elasticsearch `bool_prefix` match — each token matched
// against an indexed term, the last as a prefix — and the index does not split a hyphenated token
// the way the word cloud's own tokenizer does. So a word that sits mid-token is unreachable:
// "acute outcome" sends "outcome", which never matches a note reading "acute-outcome", and the
// drill-down lists fewer notes than the count claims. "patient outcome" escapes only because its
// words tie in length and the first one wins. No choice of word fixes this from here — a
// single-word term has nothing else to send — so the fix belongs server-side, where countWords
// already knows which activities it matched. Tracked separately.
const longestWord = (form: string): string =>
  form
    .trim()
    .split(/\s+/)
    .reduce((longest, word) => (word.length > longest.length ? word : longest), "");

const noteFilterTerms = (forms: string[]): string[] => {
  // Shortest first, so a term already covered by a shorter one is dropped:
  // every note containing "outcomes" contains "outcome".
  const words = uniq(forms.map(longestWord))
    .filter(isNotEmptyString)
    .sort((left, right) => left.length - right.length);

  // Covered means *prefixed*, not merely contained. `$contains` resolves to a `bool_prefix` match,
  // which extends a term only to its right: "outcome" reaches "outcomes", but "worker" never
  // reaches "coworker" — that word sits mid-token. Dropping on containment lost the joined
  // spelling of a phrase, which the server counts for "co worker" and could then never list.
  return words.reduce<string[]>(
    (terms, word) => (terms.some((term) => word.startsWith(term)) ? terms : [...terms, word]),
    []
  );
};

export default noteFilterTerms;
